// "Add Shared Device" — scan someone's share QR (a location, a room or a device)
// and get access to it. Their phone or desktop shows the code: ⋮ → Share (QR).
//
//   1. read     live camera · a photo of the QR · or the code pasted
//   2. preview  "Sanjay is sharing Home — 3 rooms, 14 devices" — NOTHING written yet
//   3. add      the server maps location → rooms → devices; this phone's cache is
//               rebuilt before the result shows (core/share/shareAccess)
//
// ⚠️ The preview is not a formality: a QR can be anything, and the person
// scanning should see what it is and from whom before they accept it.
//
// ⚠️ The live camera needs the CAMERA permission, which arrives with a store
// build. Until then it fails here, and the photo buttons are the way in — they
// need no permission of the app's own (core/share/qrScan). That is why they are
// always shown, not only after a failure.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, TextField, Typography, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ImageIcon from '@mui/icons-material/Image';
import { useNavigate, useLocation } from 'react-router-dom';
import { CenterTitleBar } from '../components/MobileAppBar';
import { useApp } from '../../../core/state/AppContext';
import {
  findShareCode, isLegacyShareCode, previewShare, redeemShare, describeShare,
} from '../../../core/share/shareAccess';
import { cameraAvailable, decodeImageFile, startCameraScan } from '../../../core/share/qrScan';

const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** "3 new rooms, 14 new devices" — or null when nothing is new. */
function newPart(p) {
  const a = p.already || {};
  const parts = [];
  if (p.location && !a.location) parts.push(`the location ${p.location.name}`);
  const rooms = Math.max(0, (p.rooms || 0) - (a.rooms || 0));
  const devices = Math.max(0, (p.devices || 0) - (a.devices || 0));
  if (rooms) parts.push(plural(rooms, 'new room'));
  if (devices) parts.push(plural(devices, 'new device'));
  return parts.length ? parts.join(', ') : null;
}

export default function ScanShareScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const { realUserId, userId, loadLocations } = useApp();
  // The signed-in person receives the access — never a user being viewed AS.
  const me = realUserId || userId;

  // step: 'scan' | 'checking' | 'preview' | 'adding' | 'done' | 'error'
  const [step, setStep] = useState('scan');
  const [code, setCode] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState('');        // a soft note under the scanner
  const [cameraError, setCameraError] = useState(null);
  const [pasted, setPasted] = useState('');

  const videoRef = useRef(null);
  const stopRef = useRef(null);
  const photoRef = useRef(null);
  const galleryRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (stopRef.current) { stopRef.current(); stopRef.current = null; }
  }, []);

  const notShareCode = useCallback((text) => setMessage(isLegacyShareCode(text)
    ? 'That is an old share code, which is no longer accepted. Ask for a new QR — it works for 5 minutes.'
    : 'That QR is not a share code. Ask the owner to open ⋮ → Share (QR).'), []);

  // Anything read — scanned, photographed or pasted — comes through here.
  const handleText = useCallback(async (text) => {
    const found = findShareCode(text);
    if (!found) { notShareCode(text); return; }
    stopCamera();
    setMessage('');
    setCode(found);
    setStep('checking');
    const p = await previewShare(me, found);
    if (!p.ok) { setResult(p); setStep('error'); return; }
    setPreview(p);
    setStep('preview');
  }, [me, notShareCode, stopCamera]);

  // The live camera, whenever we are on the scan step.
  useEffect(() => {
    if (step !== 'scan' || !cameraAvailable() || !videoRef.current) return undefined;
    let cancelled = false;
    // Any other QR in view: say so and keep scanning (false = keep looking).
    startCameraScan(videoRef.current, (text) => {
      if (cancelled) return true;
      if (!findShareCode(text)) { notShareCode(text); return false; }
      handleText(text);
      return true;
    })
      .then((stop) => {
        if (cancelled) stop();
        else { stopRef.current = stop; setCameraError(null); }
      })
      .catch((e) => {
        if (cancelled) return;
        // NotAllowedError = Android refused the camera: either the installed APK
        // predates the CAMERA permission (an OTA cannot add a permission — it
        // needs the next store build, docs/21), or the user said no in Settings.
        setCameraError(e && e.name === 'NotAllowedError'
          ? 'The live camera needs the latest app from the Play Store (or Camera allowed in Settings › Apps › Smart Life India › Permissions). Meanwhile, tap "Take a photo" and photograph the QR — that works now.'
          : "The camera couldn't be opened. Tap \"Take a photo\" and photograph the QR instead.");
      });
    return () => { cancelled = true; stopCamera(); };
  }, [step, handleText, notShareCode, stopCamera]);

  // A code handed over by another screen (pasted into "Add Device").
  useEffect(() => {
    if (navState?.code) handleText(navState.code);
    // once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                 // the same photo may be picked again
    if (!file) return;
    setMessage('Reading the photo…');
    const text = await decodeImageFile(file).catch(() => null);
    if (!text) {
      setMessage('No QR code was found in that photo. Hold the phone steady, fill the frame with the code and try again.');
      return;
    }
    handleText(text);
  };

  const add = async () => {
    setStep('adding');
    const r = await redeemShare(me, code);
    setResult(r);
    setStep(r.ok ? 'done' : 'error');
    // The location list lives in context; a new location must appear on Home.
    if (r.ok && loadLocations) loadLocations();
  };

  const restart = () => {
    setCode(null); setPreview(null); setResult(null); setMessage(''); setPasted('');
    setStep('scan');
  };

  const btn = {
    bgcolor: c.button, color: c.text, py: 1.25, borderRadius: '12px',
    textTransform: 'none', fontSize: 15, '&:hover': { bgcolor: c.button },
  };
  const outline = { color: c.text, borderColor: `${c.text}66`, borderRadius: '12px', textTransform: 'none' };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar title="Add Shared Device" showBack />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>

          {step === 'scan' && (
            <>
              <Typography sx={{ color: `${c.text}B3`, fontSize: 14, mb: 1.5 }}>
                Point the camera at the share QR on the owner&apos;s phone or computer.
                It can be a whole location, one room or one device.
              </Typography>

              {cameraAvailable() && !cameraError && (
                <Box sx={{
                  position: 'relative', width: '100%', aspectRatio: '1 / 1', borderRadius: '16px',
                  overflow: 'hidden', bgcolor: '#000', mb: 2,
                }}
                >
                  <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <Box sx={{
                    position: 'absolute', inset: '15%', border: '3px solid rgba(255,255,255,0.85)',
                    borderRadius: '12px', pointerEvents: 'none',
                  }}
                  />
                </Box>
              )}
              {cameraError && <Alert severity="info" sx={{ mb: 2 }}>{cameraError}</Alert>}

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button fullWidth startIcon={<PhotoCameraIcon />} sx={btn} onClick={() => photoRef.current?.click()}>
                  Take a photo
                </Button>
                <Button fullWidth startIcon={<ImageIcon />} sx={btn} onClick={() => galleryRef.current?.click()}>
                  Choose image
                </Button>
              </Box>
              <input ref={photoRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} />
              <input ref={galleryRef} type="file" accept="image/*" hidden onChange={onPhoto} />

              {message && <Alert severity="warning" sx={{ mb: 2 }}>{message}</Alert>}

              <Typography sx={{ color: `${c.text}99`, fontSize: 13, mb: 1 }}>
                Or paste the code (the owner taps Copy code):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth size="small" placeholder="SLS1.…" value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && pasted.trim()) handleText(pasted); }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: c.text, borderRadius: '12px',
                      '& fieldset': { borderColor: `${c.text}66` },
                    },
                  }}
                />
                <Button variant="outlined" sx={outline} disabled={!pasted.trim()} onClick={() => handleText(pasted)}>
                  Use
                </Button>
              </Box>
            </>
          )}

          {(step === 'checking' || step === 'adding') && (
            <Box sx={{ textAlign: 'center', pt: 8 }}>
              <CircularProgress sx={{ color: c.text }} />
              <Typography sx={{ color: c.text, mt: 2 }}>
                {step === 'checking' ? 'Checking the code…' : 'Adding…'}
              </Typography>
            </Box>
          )}

          {step === 'preview' && preview && (
            <Box sx={{ pt: 2 }}>
              <Typography sx={{ color: `${c.text}B3`, fontSize: 14 }}>
                {`${preview.byName} is sharing`}
              </Typography>
              <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 700, mt: 0.5 }}>
                {describeShare(preview)}
              </Typography>
              {preview.scope === 'location' && (
                <Typography sx={{ color: `${c.text}B3`, fontSize: 14, mt: 1 }}>
                  Every room and every device in this location, door locks included.
                </Typography>
              )}
              <Typography sx={{ color: c.text, fontSize: 15, mt: 2 }}>
                {newPart(preview)
                  ? `You will get ${newPart(preview)}.`
                  : 'You already have all of this.'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 4 }}>
                <Button fullWidth variant="outlined" sx={outline} onClick={restart}>Cancel</Button>
                <Button fullWidth sx={btn} onClick={add} disabled={!newPart(preview)}>Add</Button>
              </Box>
            </Box>
          )}

          {(step === 'done' || step === 'error') && result && (
            <Box sx={{ textAlign: 'center', pt: 4 }}>
              {result.ok
                ? <CheckCircleIcon sx={{ fontSize: 72, color: '#4CAF50' }} />
                : <ErrorOutlineIcon sx={{ fontSize: 72, color: '#F44336' }} />}
              <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 700, mt: 2 }}>
                {result.ok ? 'Added' : "Couldn't Add"}
              </Typography>
              <Typography sx={{ color: `${c.text}B3`, mt: 1.5 }}>
                {result.ok
                  ? `${describeShare(result)} is now in your account.`
                  : result.message}
              </Typography>
              <Button
                onClick={() => (result.ok ? navigate('/home?tab=1') : restart())}
                sx={{ ...btn, mt: 4, px: 3 }}
              >
                {result.ok ? 'Done' : 'Scan Again'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
